import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, PlayCircle, Headphones, FileText } from 'lucide-react';
import { GENZ_SERIES } from './genzContent';
import { GenZOverview } from './GenZOverview';
import { getDirectUrl } from '../../utils/mediaUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LessonStrip = ({ lessons }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="genz-lesson-strip">
    {lessons.map((l) => (
      <Link
        key={l.id}
        to={`/gen-z/${l.slug}`}
        className="group bg-white/5 border border-white/10 rounded overflow-hidden hover:border-brand-accent hover:bg-white/10 transition-colors duration-300"
        data-testid={`genz-strip-lesson-${l.order}`}
      >
        <div className="aspect-[4/3] bg-black/30 overflow-hidden">
          <img src={getDirectUrl(l.thumbnail_url)} alt={l.title} loading="lazy" decoding="async" className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
        </div>
        <div className="p-3">
          <p className="text-[10px] font-semibold tracking-[0.2em] text-brand-accent">LESSON {l.order}</p>
          <p className="font-heading text-sm md:text-base text-white leading-snug mt-1">{l.title}</p>
        </div>
      </Link>
    ))}
  </div>
);

export const GenZFeature = () => {
  const [lessons, setLessons] = useState([]);

  useEffect(() => {
    axios.get(`${API}/genz/lessons`).then(({ data }) => setLessons(data)).catch(() => setLessons([]));
  }, []);

  return (
    <section className="relative bg-brand-primary py-20 md:py-28 overflow-hidden" data-testid="genz-feature-section">
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-brand-accent/10 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-14">
          <Link to="/gen-z" className="lg:col-span-5 block fade-up" data-testid="genz-series-thumbnail-link">
            <img
              src={GENZ_SERIES.thumbnail}
              alt={`${GENZ_SERIES.title} series thumbnail`}
              decoding="async"
              className="w-full max-w-md mx-auto rounded shadow-2xl ring-1 ring-white/10 hover:scale-[1.02] transition-transform duration-500"
              data-testid="genz-series-thumbnail"
            />
          </Link>
          <div className="lg:col-span-7 fade-up stagger-1">
            <span className="inline-block px-3 py-1 mb-5 text-xs font-semibold tracking-[0.25em] uppercase text-brand-primary bg-brand-accent rounded" data-testid="genz-featured-badge">
              Featured Series
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05] mb-4" data-testid="genz-title">
              {GENZ_SERIES.title}
            </h2>
            <p className="font-body text-base md:text-lg font-medium text-brand-accent mb-6" data-testid="genz-subtitle">
              {GENZ_SERIES.subtitle}
            </p>
            <GenZOverview />
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <Link to="/gen-z" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-accent text-white font-body font-semibold rounded hover:bg-brand-accent-hover transition-colors duration-300" data-testid="genz-view-series-button">
                View All Lessons <ArrowRight className="h-4 w-4" />
              </Link>
              <div className="flex items-center gap-4 text-white/70 text-sm font-body">
                <span className="inline-flex items-center gap-1.5"><PlayCircle className="h-4 w-4 text-brand-accent" /> Video</span>
                <span className="inline-flex items-center gap-1.5"><Headphones className="h-4 w-4 text-brand-accent" /> MP3</span>
                <span className="inline-flex items-center gap-1.5"><FileText className="h-4 w-4 text-brand-accent" /> PDF</span>
              </div>
            </div>
          </div>
        </div>
        {lessons.length > 0 && <LessonStrip lessons={lessons} />}
      </div>
    </section>
  );
};
